import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { mediaSource } from "../utilities/mediautilities";
import { FaArrowRight, FaPercentage } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "../languages/PlaceHolder.js";
import Breadcrumb from "../components/Breadcrumb.jsx";
import { setBrowserTitle } from "../utilities/codes.js";

const TopStudents = () => {
    const [selectedStudent, setSelectedStudent] = useState(null);
    const { schoolDetails, topperStudents } = useContext(schoolContext);
    const { t } = useTranslation();

    useEffect(() => {
        setBrowserTitle("Top Student - Honors")
    }, [])

    return (
        <>
            <section className="relative bg-cover bg-center py-36 px-6">
                <img
                    src={mediaSource(schoolDetails?.dignataries?.fileUrl)}
                    alt={"Page banner"}
                    className="absolute inset-0 w-full h-full object-cover"
                />
            </section>
            <Breadcrumb />

            <section className="py-12 md:py-16 px-4 md:px-6 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                        {topperStudents?.map((student, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="h-full"
                                onClick={() => setSelectedStudent(student)}
                            >
                                {/* Main Card: Removed hover shadow and lift classes */}
                                <div className="bg-white rounded-2xl shadow-sm hover:shadow-md h-full flex flex-col border border-gray-200 p-6 items-center text-center cursor-pointer">

                                    {/* Profile Image Section */}
                                    <div className="relative mb-4">
                                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-50 shadow-inner">
                                            <img
                                                src={mediaSource(student?.payload?.name_ref?.profile_photo?.fileUrl)}
                                                alt={student?.payload?.name_ref?.student_name || "Student"}
                                                className="w-full h-full object-cover"
                                                onError={(e) => mediaSource(e)}
                                            />
                                        </div>
                                    </div>

                                    {/* Content Section */}
                                    <div className="flex-1 flex flex-col w-full">
                                        <h3 className="text-xl text-gray-900 mb-1">
                                            {student?.payload?.full_name || student?.payload?.name_ref?.full_name}
                                        </h3>

                                        <p className="text-sm text-gray-500 mb-4">
                                            {t(I18N_KEYS.CLASS)} {student?.payload?.name_ref?.class_ref?.class}
                                        </p>

                                        {/* Percentage Box */}
                                        {student?.payload?.percentage && (
                                            <div className="rounded-lg py-2 px-4 mb-4 inline-block mx-auto">
                                                <span className="text-2xl font-black text-primary flex items-center justify-center gap-1 text-md">
                                                    {student?.payload?.percentage}<FaPercentage className="text-sm" />
                                                </span>
                                                <p className="text-[10px] uppercase tracking-widest text-gray-400">
                                                    {t(I18N_KEYS.PERCENTAGE)}
                                                </p>
                                            </div>
                                        )}

                                        {/* Achievement Text */}
                                        {student?.payload?.achievement && (
                                            <div className="mb-2 px-2">
                                                <p className="text-sm text-gray-600 italic">
                                                    "{student?.payload?.achievement}"
                                                </p>
                                            </div>
                                        )}

                                        {/* Connection-style Button */}
                                        <div className="mt-auto flex justify-center">
                                            <span className="inline-flex items-center gap-2 px-8 py-2 rounded-full border-2 border-primary text-primary text-sm">
                                                {t(I18N_KEYS.VIEWPROFILE)}
                                                <FaArrowRight className="text-xs" />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Modal for detailed view */}
                <AnimatePresence>
                    {selectedStudent && (
                        <motion.div
                            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedStudent(null)}
                        >
                            <button
                                onClick={() => setSelectedStudent(null)}
                                className="absolute top-4 right-4 text-accent text-3xl font-bold hover:text-primary z-10 w-12 h-12 flex items-center justify-center bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                            >
                                &times;
                            </button>
                            <motion.div
                                className="bg-accent rounded-3xl overflow-hidden shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                                initial={{ scale: 0.8, y: 40 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.8, y: 40 }}
                                transition={{ duration: 0.3 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-8 md:p-12">
                                    <div className="flex justify-center mb-8">
                                        <div className="relative w-48 h-48 md:w-64 md:h-64">
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-primary rounded-full p-1">
                                                <div className="w-full h-full bg-accent rounded-full overflow-hidden border-4 border-accent">
                                                    <img
                                                        src={mediaSource(selectedStudent?.payload?.name_ref?.profile_photo?.fileUrl)}
                                                        alt={selectedStudent?.payload?.name_ref?.full_name || "Student"}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => mediaSource(e)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        {selectedStudent?.payload?.name_ref?.full_name && (
                                            <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-800">
                                                {selectedStudent?.payload?.name_ref?.full_name}
                                            </h3>
                                        )}
                                        {selectedStudent?.payload?.name_ref?.class_ref?.class && (
                                            <div className="inline-flex items-center gap-2 px-6 py-2 bg-primary/10 rounded-full mb-6">
                                                <span className="text-primary font-semibold text-lg">
                                                    {t(I18N_KEYS.CLASS)} {selectedStudent?.payload?.name_ref?.class_ref?.class}
                                                </span>
                                            </div>
                                        )}
                                        {selectedStudent?.payload?.percentage && (
                                            <div className="mb-6 flex justify-center flex-col">
                                                <span className="text-4xl font-black text-secondary flex items-center justify-center">
                                                    {selectedStudent?.payload?.percentage}<FaPercentage />
                                                </span>
                                                <p className="text-gray-600 mt-2">{t(I18N_KEYS.OVERALLPER)}</p>
                                            </div>
                                        )}
                                        {selectedStudent?.payload?.achievement && (
                                            <div className="bg-secondary/10 rounded-xl p-4 mb-6 border border-secondary/30">
                                                <h4 className="font-semibold text-secondary mb-2">{t(I18N_KEYS.ACHIEVEMENT)}</h4>
                                                <p className="text-secondary font-medium">{selectedStudent?.payload?.achievement}</p>
                                            </div>
                                        )}
                                        {selectedStudent?.payload?.description && (
                                            <div className="bg-primary/5 rounded-xl p-6 mb-6">
                                                <h4 className="font-semibold text-primary mb-3 text-left">{t(I18N_KEYS.ABOUT)}</h4>
                                                <p className="text-gray-700 leading-relaxed text-left"
                                                    dangerouslySetInnerHTML={{
                                                        __html: selectedStudent?.payload?.description
                                                    }}
                                                >
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </section>
        </>
    );
}
export default TopStudents;